import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import { useForm } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';

const allowedRoles = ['developer', 'school_admin', 'school_moderator'];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithPin, userRole } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState(0);
  const [deviceId, setDeviceId] = useState('');
  const [pin, setPin] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [roleError, setRoleError] = useState('');
  
  // Get redirect path from location state or default to dashboard
  const from = location.state?.from || '/dashboard';
  
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    submitError,
    setFieldValue
  } = useForm(
    {
      email: '',
      password: '',
      rememberMe: false
    },
    ['email', 'password'],
    async (formData) => {
      // Call login function from auth context
      await login(formData.email, formData.password);
      // Always navigate to the redirect path after successful login
      navigate(from, { replace: true });
    },
    'login'
  );
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleTabChange = (_, newValue) => {
    setTab(newValue);
    setRoleError('');
    setDeviceError('');
  };
  
  const handleDeviceLogin = async (e) => {
    e.preventDefault();
    setDeviceError('');
    setDeviceLoading(true);
    try {
      const userCredential = await loginWithPin(deviceId, pin);
      if (userCredential && userCredential.user && userCredential.user.role !== 'teacher') {
        setDeviceError('Only teachers can log in with device ID.');
        setDeviceLoading(false);
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setDeviceError('Invalid device ID or PIN.');
    }
    setDeviceLoading(false);
  };
  
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={handleTabChange} centered>
              <Tab label="Admin / Moderator Login" icon={<EmailIcon />} iconPosition="start" />
              <Tab label="Teacher Device Login" icon={<DevicesIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          
          {tab === 0 && (
            <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/logo192.png"
              alt="SafeLine Logo"
              sx={{
                width: 60,
                height: 60,
                mb: 2
              }}
            />
            
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Welcome Back
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
            >
              Sign in to your SafeLine admin account
            </Typography>
          </Box>
          
          {/* Error message */}
          {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
              {roleError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {roleError}
                </Alert>
              )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              variant="outlined"
              margin="normal"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
                mb: 2
              }}
            >
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                underline="hover"
              >
                Forgot password?
              </Link>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isSubmitting}
              sx={{ 
                py: 1.5,
                mt: 1,
                mb: 2,
                position: 'relative'
              }}
            >
              {isSubmitting ? (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
            </>
          )}
          
          {tab === 1 && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Box component="img" src="/logo192.png" alt="SafeLine Logo" sx={{ width: 60, height: 60, mb: 2 }} />
                <Typography variant="h5" mb={2}>Teacher Device Login</Typography>
              </Box>
              {deviceError && <Alert severity="error" sx={{ mb: 2 }}>{deviceError}</Alert>}
              <form onSubmit={handleDeviceLogin}>
                <TextField
                  fullWidth
                  label="Device ID"
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value)}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <KeyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  required
                />
                <TextField
                  fullWidth
                  label="PIN"
                  type="password"
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={deviceLoading}
                  sx={{ py: 1.5, mt: 1, mb: 2, position: 'relative' }}
                >
                  {deviceLoading ? (
                    <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} />
                  ) : (
                    'Login'
                  )}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Teachers must use their assigned device and PIN. No admin dashboard access.
                </Typography>
              </form>
            </>
          )}
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Don't have an account?
            </Typography>
            
            <Button
              component={RouterLink}
              to="/signup"
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
            >
              Create New Account
            </Button>
          </Box>
        </Paper>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          &copy; {new Date().getFullYear()} SafeLine. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default Login;
